import React, { useState, useEffect, useMemo } from "react";
import { Button, Container, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import styled from "styled-components";
import MultipleIndicator from "./MultipleIndicator";
import CheckboxQuestion from "./CheckboxQuestion";
import YesNo from "./YesNo";
import TextQuestion from "./TextQuestion";
import { getAllQuestions } from "../../../apis/questionnaire";
import { toastify } from "../../../helperFunctions/toastify";
import { authorizedRequests } from "../../../axios/axios-config";

import { SignWrapper } from "./styled";

const StyledTitle = styled.p`
	text-align: center;
	color: #b3914c;
	font-size: 20px;
	font-weight: 600;
	margin-top: 20px;
`

function Questionnaire() {
	let navigate = useNavigate();
	let params = useParams();
	const { id } = params;
	// const [id, setId] = useState();
	const today = new Date().toLocaleDateString("en-GB", {
		day: "numeric",
		month: "numeric",
		year: "numeric",
	});

	const [questions, setQuestions] = useState([]);
	const [allQuestions, setAllQuestions] = useState([]);
	const [edit, setEdit] = useState(null);
	const [patientRef, setPatientRef] = useState({});
	const [staffRef, setStaffRef] = useState({});
	const [resUser, setResUser] = useState(null);

	const getSignDate = (type) => {
		if (type === "Patient") {
			return today;
		} else {
			return today;
		}
	}

	const renderQuestions = (question, grpQuestion) => {
		if (question?.questionType === "Multiple Indicators")
			return (
				<MultipleIndicator question={question} grpQuestion={grpQuestion} onChange={onAnsChange} />
			);
		else if (question?.questionType === "Yes/No")
			return <YesNo question={question} grpQuestion={grpQuestion} onChange={onAnsChange} />;
		else if (question?.questionType === "Text")
			return <TextQuestion question={question} grpQuestion={grpQuestion} onChange={onAnsChange} />;
		else if (question?.questionType === "Checkbox")
			return <CheckboxQuestion question={question} grpQuestion={grpQuestion} onChange={onAnsChange} />
	};

	const getQuestions = async (answers) => {
		const response = await getAllQuestions();
		let allChildQuestions = [];
		if (response?.status !== "Fail") {
			const mapQuestions = response.data?.map((x => {
				const items = x.data
				.filter((e) => e.isActive === true)
				.map((o) => {
					if (answers?.length == 0) {
						var pat_ans =
							(o?.questionType == "Multiple Indicators" || o?.questionType == "Checkbox")
								? []
								: "";
						return {
							...o,
							patient_answer: pat_ans,
						};
					}

					const ans = answers?.find((k) => {
						return k?.question?._id === o._id;
					});

					pat_ans =
						o?.questionType == "Yes/No"
							? ans?.booleanAnswer
							: o?.questionType == "Text"
							? ans?.textAnswer
							: o?.questionType == "Checkbox"
							? ans?.checkboxAnswers
							: ans?.multiAnswers;
					return {
						...o,
						patient_answer: pat_ans,
					};
				});

				allChildQuestions = allChildQuestions.concat(items);

				return {
					group: x._id,
					questions: items
				}
			}));

			mapQuestions.sort((a,b) => a.questions[0].orderIndex - b.questions[0].orderIndex);

			setQuestions(mapQuestions);
			setAllQuestions(allChildQuestions);
		} else {
			if (response?.message === "No data Found") {
				setQuestions([]);
			} else {
				toastify(response?.message, "error");
			}
		}
	};

	const submitForm = async () => {
		const tempArr = allQuestions?.map((o) => {
			if (o.questionType == "Text") {
				return {
					question: o?._id,
					questionType: o?.questionType,
					linkedTo: o?.linkedTo,
					textAnswer: o?.patient_answer,
				};
			} else if (o.questionType == "Yes/No") {
				return {
					question: o?._id,
					questionType: o?.questionType,
					linkedTo: o?.linkedTo,
					booleanAnswer: o?.patient_answer,
				};
			} else if (o.questionType == "Checkbox") {
				return {
					question: o?._id,
					questionType: o?.questionType,
					linkedTo: o?.linkedTo,
					checkboxAnswers: o?.patient_answer,
				};
			} else {
				return {
					question: o?._id,
					linkedTo: o?.linkedTo,
					multiAnswers: o?.patient_answer?.filter(
						(k) => k?.answers.length > 0,
					),
				};
			}
		});
		console.log(tempArr)
		if (edit) {
			const { result, error } = await authorizedRequests({
				url: "usersQuestionnaire/updateUserQuestionnaireById",
				method: "post",
				data: {
					id: edit,
					questionData: [...tempArr],
					patientSign: patientRef.toDataURL("image/png"),
					staffSign: staffRef.toDataURL("image/png")
				},
			});
			if (error) {
				toastify(
					error.message ?? "Failed to post questionnaire form",
					"error",
				);
				return;
			}
			toastify("Thank You!", "success");
			navigate(`/medicalform/${id}`);
			return;
		}
		const { result, error } = await authorizedRequests({
			url: "usersQuestionnaire/createUsersQuestionnaire",
			method: "post",
			data: {
				questionData: [...tempArr],
				patient: id,
				staffSign: staffRef.toDataURL("image/png"),
				staffSign: staffRef.toDataURL("image/png")
			},
		});
		if (error) {
			toastify(
				error.message ?? "Failed to post questionnaire form",
				"error",
			);
			return;
		}
		toastify("Thank You!", "success");
		navigate(`/medicalform/${id}`);
	};

	function onAnsChange(id, ans, type, typeGroup) {
		const tempArr = [...questions];
		const groupIdx = tempArr.findIndex((o) => o.group == typeGroup);
		if (groupIdx >= 0) {
			const index = tempArr[groupIdx].questions.findIndex((o) => o._id == id);
			if (type !== "Multiple Indicators") {
				if (type === "Checkbox") {
					if (tempArr[groupIdx].questions[index].patient_answer?.filter(x => x === ans).length > 0) {
						tempArr[groupIdx].questions[index].patient_answer = tempArr[groupIdx].questions[index].patient_answer.filter(x => x !== ans);
					} else {
						tempArr[groupIdx].questions[index].patient_answer.push(ans);
					}
					setQuestions(tempArr);
				} else {
					tempArr[groupIdx].questions[index].patient_answer = ans;
					setQuestions(tempArr);
				}
			} else {
				const indicatorIndex = tempArr[groupIdx].questions[index]?.patient_answer?.findIndex(
					(o) => o.indicator == ans.indicator,
				);
				if (indicatorIndex == -1) {
					tempArr[groupIdx].questions[index].patient_answer.push({
						indicator: ans.indicator,
						answers: [ans.label],
					});
					setQuestions(tempArr);
				} else {
					let ansArr =
						tempArr[groupIdx].questions[index].patient_answer[indicatorIndex].answers;
					const ansIndex = ansArr.indexOf(ans.label);
					if (ansIndex === -1) {
						ansArr.push(ans.label);
						tempArr[groupIdx].questions[index].patient_answer[indicatorIndex].answers =
							ansArr;
						setQuestions(tempArr);
					} else {
						ansArr = ansArr.filter((o) => o != ans.label);
						tempArr[groupIdx].questions[index].patient_answer[indicatorIndex].answers =
							ansArr;
						setQuestions(tempArr);
					}
				}
			}
		}
	}
	// useEffect(() => {
	//   if (params?.id) {
	//     setId(params.id);
	//   }
	// }, [params.id]);

	useEffect(() => {
		(async () => {
			const { result, error } = await authorizedRequests({
				url: "usersQuestionnaire/getUserQuestionnaireByPatientId",
				method: "post",
				data: {
					patient: id,
				},
			});
			if (error) {
				setEdit();
				getQuestions([]);
				return;
			}
			setEdit(result.data?._id);
			getQuestions(result.data?.questionData);
			setResUser(result.data);
		})();
	}, []);

	console.log(questions)

	return (
		<div className=" px-5 mb-5 pb-5 white-background">
			<div className="d-flex w-100 justify-content-center p-0 questionnaire-heading my-5 flex-column align-items-center">
				<div className="logo-toggle my-3">
					<img src="/imgs/logo.svg" className="dpImg" alt="" />
				</div>
				<div> Questionnaire Form</div>
			</div>
			<Container fluid>
				<Row>
					{
						questions?.map((q, index) => (
							<React.Fragment>
								<StyledTitle>{q.group}</StyledTitle>
								{
									q.questions?.map((question, childIdx) => (
										renderQuestions(question, q.group)
									))
								}
							</React.Fragment>
						))
					}
				</Row>
				<Row className="mt-4">
					<Col md={6}>
						<SignWrapper className="patient-signature">
							<div className="text-center p-3 grey header-sign">
								<p>Patient Signature</p>
								<p>{getSignDate("Patient")}</p>
							</div>
							<SignatureCanvas
								penColor="green"
								ref={(ref) => {
									ref?.fromDataURL(
										resUser?.patientSign
									);

									setPatientRef(ref);
								}}
								canvasProps={{
									width: 300,
									height: 150,
									className: "sigCanvas",
								}}
							/>

						</SignWrapper>
						<div style={{width: 300}}>
							<Button
								className="grey-rounded-button ms-auto mt-3"
								onClick={() => { patientRef.clear() }}
							>
								Clear Box
							</Button>
						</div>
					</Col>
					<Col md={6}>
						<SignWrapper className="patient-signature" style={{marginLeft: "auto"}}>
							<div className="text-center p-3 grey header-sign">
								<p>Staff Signature</p>
								<p>{getSignDate("Staff")}</p>
							</div>
							<SignatureCanvas
								penColor="green"
								ref={(ref) => {
									ref?.fromDataURL(
										resUser?.staffSign
									);

									setStaffRef(ref);
								}}
								canvasProps={{
									width: 300,
									height: 150,
									className: "sigCanvas",
								}}
							/>

						</SignWrapper>
						<div style={{width: 300, marginLeft: "auto"}}>
							<Button
								className="grey-rounded-button ms-auto mt-3"
								onClick={() => { staffRef.clear() }}
							>
								Clear Box
							</Button>
						</div>
					</Col>
				</Row>
			</Container>
			<div className="d-flex justify-content-center">
				{allQuestions?.length > 0 && (
					<Button
						className="addItem"
						onClick={() => {
							submitForm();
							// navigate("/patient");
						}}
					>
						Continue
					</Button>
				)}
			</div>
		</div>
	);
}

export default Questionnaire;
